import {
  Box,
  Divider,
  Icon,
  Modal,
  Typography,
  Card,
  TextField,
  Button,
  FormHelperText,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

export function EvidenceCreate({
  open,
  handleClose,
  onSubmit,
  isFieldValid,
  handleChangeValid,
  fileErrors,
  formState,
  handleFormStateChange,
  isFormValid,
  validateFile,
}) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "600px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  // Límite máximo de caracteres para la descripción
  const maxLength = 250;
  // Estilo personalizado para texto de éxito
  const successTextStyle = {
    "& .Mui-success": {
      color: "#2e7d32 !important",
    },
  };

  const [formValues, setFormValues] = useState({
    description: "",
    file: null,
  });

  const [errors, setErrors] = useState({
    description: false,
    file: false,
  });

  /**
   * Maneja los cambios en los campos del formulario
   * Aplica formato capitalizado a la descripción y valida el campo
   * @param {Event} event - Evento del input
   */
  // Función helper para obtener valores de los campos (ahora usa formState del controlador)
  const getFieldValue = (fieldName) => {
    return formState[fieldName] || formValues[fieldName];
  };

  // Función helper para manejar cambios en los campos
  const handleFieldChange = (fieldName, value) => {
    if (fieldName === "description") {
      const formattedValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      setFormValues({
        ...formValues,
        [fieldName]: formattedValue,
      });
      handleChangeValid(fieldName, formattedValue);
      handleFormStateChange(fieldName, formattedValue);
    }
  };

  const handleChange = (event) => {
    const { name, value, files } = event.target;

    // Formatear la descripción si es el campo correspondiente
    const formattedValue =
      name === "description" ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

    if (name === "file" && files && files[0]) {
      const file = files[0];

      // Usar la validación del controlador
      validateFile(file);

      // Actualizar los estados
      setFormValues({
        ...formValues,
        file: file,
      });
      handleFormStateChange("file", file);
      return;
    }

    // Para otros campos que no sean archivos
    setFormValues({
      ...formValues,
      [name]: files ? files[0] : formattedValue,
    });

    if (name === "description") {
      handleChangeValid(name, formattedValue);
      handleFormStateChange(name, formattedValue);
    }
  };

  const validateForm = () => {
    const newErrors = {
      description: !formValues.description, // Error si está vacío
      file: !formValues.file, // Error si no se ha subido un archivo
    };

    setErrors(newErrors);

    // Usar la validación del controlador
    return isFormValid();
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    // Validar el formulario
    if (validateForm()) {
      onSubmit(formValues); // Si la validación pasa, llama a onSubmit
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        setFormValues({ description: "", file: null });
        setErrors({ description: false, file: false });
        handleClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Card sx={{ ...style, ...successTextStyle }}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Typography id="modal-modal-title" component="h2">
            Crear evidencia
          </Typography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              marginLeft: "auto",
            }}
            onClick={() => {
              setFormValues({ description: "", file: null });
              setErrors({ description: false, file: false });
              handleClose();
            }}>
            close
          </Icon>
        </MDBox>
        <Divider variant="middle" />
        <Box component="form" onSubmit={handleFormSubmit} sx={{ mt: 2 }}>
          <Box mb={2}>
            <TextField
              fullWidth
              label="Descripción de la evidencia"
              variant="outlined"
              name="description"
              value={formValues.description}
              onChange={(e) => handleFieldChange("description", e.target.value)}
              multiline
              rows={4}
              helperText={
                getFieldValue("description") === "" && errors.description
                  ? "La descripción es obligatoria"
                  : !isFieldValid.description
                    ? "No se permiten números ni caracteres especiales"
                    : `${getFieldValue("description")?.length || 0} / 500 caracteres`
              }
              FormHelperTextProps={{
                className: errors.description || !isFieldValid.description ? "Mui-error" : "",
              }}
              required
            />
          </Box>
          {/* <Button variant="gradient" component="label" sx={{ mb: 2 }}>
            Subir archivo
            <input type="file" name="file" hidden onChange={handleChange} />
          </Button> */}
          <Box mb={2}>
            <MDInput
              fullWidth
              sx={{ mt: 0 }}
              type="file"
              accept="image/*,video/*,audio/*"
              helperText={
                !formValues.file && fileErrors.file
                  ? "Es obligatorio seleccionar un archivo"
                  : fileErrors.fileType
                    ? "Archivo no válido. Solo se permiten imágenes, videos y audios"
                    : fileErrors.fileSize
                      ? fileErrors.fileSizeMessage || "El tamaño del archivo no es permitido"
                      : "Seleccione una imagen, video o archivo de audio como evidencia"
              }
              FormHelperTextProps={{
                className:
                  fileErrors.file || fileErrors.fileType || fileErrors.fileSize ? "Mui-error" : "",
              }}
              name="file"
              onChange={handleChange}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={
                        <div>
                          <strong>¿Qué puedo subir?</strong>
                          <div>Adjunte un archivo que sirva como evidencia de la actividad.</div>
                          <br />
                          <strong>Formatos y tamaños máximos</strong>
                          <ul>
                            <li>Imágenes: JPG, PNG, WebP — hasta 10 MB</li>
                            <li>Audios: MP3, WAV, OGG, M4A — hasta 10 MB</li>
                            <li>Videos: MP4, MOV, WebM — hasta 100 MB</li>
                          </ul>
                        </div>
                      }
                      arrow
                      placement="top">
                      <IconButton size="small" tabIndex={-1}>
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider variant="middle" />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, gap: 1 }}>
            <MDButton
              color="error"
              sx={{
                flex: 1,
              }}
              onClick={() => {
                setFormValues({ description: "", file: null });
                setErrors({ description: false, file: false });
                handleClose();
              }}>
              Cancelar
            </MDButton>

            <MDButton
              type="submit"
              color="success"
              disabled={!isFormValid()}
              sx={{
                flex: 1,
                opacity: !isFormValid() ? 0.6 : 1,
                cursor: !isFormValid() ? "not-allowed" : "pointer",
              }}>
              Guardar
            </MDButton>
          </Box>
          {/* <MDButton type="submit" variant="gradient" color="info" fullWidth>
            Crear evidencia
          </MDButton> */}
        </Box>
      </Card>
    </Modal>
  );
}
